import React from 'react';
import { Card, CardContent } from './ui/card';
import { Building2, Users, Target, Award } from 'lucide-react';

export const AboutSection = () => {
  const features = [
    {
      icon: Building2,
      title: "U.S. Registered",
      description: "Software consulting organization with proven track record"
    },
    {
      icon: Users,
      title: "Industry Focused",
      description: "Specialized in Pharmaceutical, Analytical, Chemical industries"
    },
    {
      icon: Target,
      title: "One-Stop Solution",
      description: "Integrating every department into a unified platform"
    },
    {
      icon: Award,
      title: "Customizable",
      description: "High-scale ERP solutions tailored to your needs"
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-medium mb-4">
            About Genra Tech
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Your Trusted ERP Partner
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Genra Tech is a U.S.-registered software consulting organization, dedicated to delivering 
            customizable, high-scale ERP solutions for industry leaders.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          {/* Left - Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1669216368818-8dcc28259724?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxwaGFybWFjZXV0aWNhbCUyMGxhYm9yYXRvcnl8ZW58MHx8fHwxNzU3OTA1MjEyfDA&ixlib=rb-4.1.0&q=85"
                alt="Laboratory professionals working"
                className="w-full h-96 object-cover transform hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/30 to-transparent"></div>
            </div>
            
            {/* Floating Stats */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 border-l-4 border-teal-600">
              <div className="text-2xl font-bold text-teal-600 mb-1">GENRA ERP</div>
              <div className="text-gray-600 text-sm">One-Stop Solution</div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Unifying Your Organization
            </h3>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              Our flagship product, <strong className="text-teal-600">GENRA ERP</strong>, is designed as a one-stop 
              solution for organizational leaders—integrating every department and function into a unified platform.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              We serve industries including <strong>Pharmaceuticals</strong>, <strong>Analytical labs</strong>, 
              <strong> Chemical manufacturing</strong>, and <strong>Chemistry services</strong>, with tailored 
              modules that ensure efficiency, compliance, and growth.
            </p>

            <div className="bg-teal-50 rounded-xl p-6 border-l-4 border-teal-600 mt-8">
              <h4 className="font-semibold text-gray-900 mb-2">Our Mission</h4>
              <p className="text-gray-700">
                To empower organizations with intelligent ERP solutions that drive efficiency, 
                ensure compliance, and accelerate growth in specialized industries.
              </p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
              <CardContent className="p-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
                  <feature.icon className="h-8 w-8 text-teal-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};